"use client";
import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/browser";
import { isResponseCsv, serviceHasError, serviceIsFetched, serviceIsLoadingOrFetched } from "./seviceUtils";
import { differenceInSeconds } from "date-fns";

let counter = 0;

export const SILENT_RE_FETCH_TIMER = 60000;

export default function useServiceHook({
    appProps,
    system = appProps?.system,
    fetchFn,
    fetchData = {}, // Data supplied here will be sent on every request to the service
    fetchRequested = true,
    onReceive,
    onError,
    handleResponse,
    silentReFetchTimer,
}) {
    const [serviceData, setServiceData] = useState({
        fetching: false,
        fetchRequested,
        fetched: false,
        silentFetch: false, // data is only removed when new data or error comes
        data: null,
        error: null,
        timesFetched: 0,
    });
    const [responseData, setResponseData] = useState(null);
    const staticFetchData = fetchData;
    const [localFetchData, setLocalFetchData] = useState(fetchData);

    if (!system) {
        // console.error("BAD! Using useServiceHook without system. Fix immediately!")
    }
    useEffect(() => {
        if (serviceData.fetchRequested) {
            counter += 1;
            const callId = counter;
            setServiceData({
                fetching: true,
                fetchRequested: false,
                fetched: serviceData?.silentFetch ? serviceData?.fetched : false,
                data: serviceData?.silentFetch ? serviceData?.data : null,
                silentFetch: serviceData?.silentFetch,
                error: null,
                callId: callId,
                timesFetched: (serviceData?.timesFetched || 0) + 1,
                lastFetchedDate: new Date(),
            });
            fetchFn(system, localFetchData)
                .then((response) => {
                    return setResponseData({ response, callId });
                })
                .catch((error) => {
                    return setResponseData({ error: error, callId });
                });
        }
    }, [serviceData]);

    useEffect(() => {
        if (responseData && (serviceData.fetching || serviceData.silentFetch) && serviceData.callId === responseData.callId) {
            responseAndErrorHandling({
                response: responseData?.response,
                error: responseData?.error,
                handleResponse,
                callId: responseData?.callId,
                onReceive,
                onError,
                sentry: true,
                silentFetch: responseData?.silentFetch,
                oldData: serviceData,
                fetchData: localFetchData,
            }).then((handledData) => {
                setServiceData({
                    ...handledData,
                    timesFetched: serviceData?.timesFetched,
                    lastFetchedDate: serviceData?.lastFetchedDate,
                });
            });
        }
    }, [responseData, serviceData]);

    const fetchServiceData = useCallback(
        (fetchData = null, { silentFetch = false } = {}) => {
            fetchData && setLocalFetchData({ ...staticFetchData, ...fetchData });
            setServiceData({
                ...(silentFetch && serviceData),
                fetchRequested: true,
                timesFetched: serviceData?.timesFetched,
                lastFetchedDate: serviceData?.lastFetchedDate,
                silentFetch,
            });
        },
        [serviceData, setServiceData, staticFetchData],
    );

    const smartGetServiceData = useCallback(
        (fetchData = null) => {
            if (!serviceIsLoadingOrFetched(serviceData) || serviceHasError(serviceData)) {
                const seconds = differenceInSeconds(Date.now(), new Date(serviceData?.lastFetchedDate));
                if (serviceData?.timesFetched < 3 || (serviceData?.lastFetchedDate && seconds > 60)) {
                    fetchServiceData(fetchData);
                }
            }
            return serviceData;
        },
        [serviceData, fetchServiceData],
    );

    useEffect(() => {
        let timeoutId;
        if (serviceIsFetched(serviceData) && silentReFetchTimer) {
            const timeoutCallback = () => {
                fetchServiceData(null, { silentFetch: true });
                timeoutId = setTimeout(timeoutCallback, silentReFetchTimer);
            };

            timeoutId = setTimeout(timeoutCallback, silentReFetchTimer);
        }
        return () => {
            clearTimeout(timeoutId);
        };
    }, [serviceData, silentReFetchTimer]);

    const clearServiceData = useCallback(() => {
        setServiceData({ fetchRequested: false, timesFetched: 0 });
    }, [setServiceData]);
    return {
        serviceData,
        fetchServiceData,
        smartGetServiceData,
        clearServiceData,
    };
}

function responseToErrorHandling({ response, error }) {
    if (!response?.ok) {
        if (error) {
            return Promise.reject(error);
        }
        // We do a backupResponse here to test .text() instead since sometimes we make calls to API,s outside our domain that do not use JSON.
        const backupResponse = response.clone();
        return response
            .json()
            .catch(() => {
                return backupResponse.text().then((text) => {
                    throw {
                        name: "Error response text",
                        response: backupResponse,
                        message: text || "Failed to fetch data",
                    };
                });
            })
            .then((data) => {
                throw {
                    name: "Error response json",
                    response: response,
                    data: data,
                    statusText: data?.statusText,
                    message: data?.message || "Failed to fetch data",
                };
            });
    }
    return response;
}

export function responseAndErrorHandling({ response, error, handleResponse, callId, onReceive, onError, sentry = false, silentFetch, oldData, fetchData }) {
    let tempRawData;

    return Promise.resolve(
        responseToErrorHandling({
            response: response,
            error: error,
        }),
    )
        .then((response) => {
            if (handleResponse) {
                return handleResponse(response);
            } else {
                return response?.text();
            }
        })
        .then((data) => {
            tempRawData = data;
            if (isResponseCsv(response)) {
                return data;
            }
            return handleResponse ? data : data ? JSON.parse(data) : {};
        })
        .then((data) => {
            sentry &&
                Sentry.setContext("Service call " + callId, {
                    url: response?.url,
                    requestData: fetchData,
                    responseData: data,
                    requestDataRaw: JSON.stringify(fetchData),
                    responseDataRaw: tempRawData,
                });
            onReceive && onReceive(data, response);
            return {
                fetching: false,
                fetched: true,
                status: response?.status,
                url: response?.url,
                data: data,
                error: null,
            };
        })
        .catch((error) => {
            const statusText = error?.statusText || "DEFAULT";
            sentry &&
                Sentry.setContext("Service call " + callId + " (Error)", {
                    url: response?.url,
                    requestData: fetchData,
                    responseError: error,
                    status: error?.status || response?.status,
                    statusText: statusText,
                    requestDataRaw: JSON.stringify(fetchData),
                    responseErrorRaw: tempRawData,
                });
            onError && onError(error);
            return {
                fetching: false,
                fetched: true,
                data: silentFetch ? oldData : null,
                status: error?.status || response?.status,
                url: response?.url,
                statusText: statusText,
                error: error.message,
                traceId: response?.headers?.get("x-trace-id"),
            };
        });
}
